import React, { useState, useMemo, useEffect, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';
import queryString from 'query-string';
import trimAddress from 'util/trimAddress';
import switchChain from 'util/switchChain';
import stroke from 'assets/images/stroke.svg';
import SunIcon from '../../assets/images/sun.png';
import Loader from 'assets/images/load_spinner.svg';
import { defaultChainId } from 'components/DummyData';
import ThemeContext from 'components/context/ThemeContext';
import alertTriangle from 'assets/images/alert_triangle.svg';
import { navOptions } from 'components/DummyData';
import useCurrentChain from 'components/Hooks/useCurrentChain';
import logo from 'assets/images/svg/VirtuSwap_text_originalWhite.svg';
import logo_Light from 'assets/images/svg/VirtuSwap_text_originalBlack.svg';
import {
    getWeb3Connected,
    getWalletInfo,
    getConnectedAddress,
    getFaucetToken,
    setIsTradeLoad,
    getPoolByAddress,
    getTokenByAddress,
    setCurrentChainId,
} from 'store/action';
import { useDisconnect, useAppKit } from '@reown/appkit/react';
import { modal } from '../WalletConnect';

const showCurrentChainName = false; // will show chain name near the icon if is set to true

const Header = () => {
    const { open: openWeb3Modal } = useAppKit();
    const { disconnect } = useDisconnect();
    const { theme, switchThemes } = useContext(ThemeContext);
    const dispatch = useDispatch();
    const { pathname, search: searchParams } = useLocation();
    const navigate = useNavigate();
    const {
        walletInfo,
        web3Connected,
        transactionLoader,
        connectedAddress,
        RecentTx,
        ChainList,
        ChainListLoader,
        currentChainId: walletChainId,
    } = useSelector((state) => state.Trade);
    const currentChain = useCurrentChain(!walletChainId, false);

    const [isProfile, setIsProfile] = useState(false);
    const [isRecentTx, setIsRecentTx] = useState([]);
    const [isRecentDefaultTx, setIsRecentDefaultTx] = useState(false);
    const [isTxFound, setIsTxFound] = useState(false);
    const [isSelectingNetwork, setIsSelectingNetwork] = useState(false);
    const [navbarCollapsed, setNavbarCollapsed] = useState(true);

    const switchableChains = useMemo(
        () => ChainList.filter(chain => !!chain.vrouter_address),
        [ChainList],
    );

    const currentChainName = useMemo(
        () => showCurrentChainName
            ? currentChain?.name?.split(' ')?.[0]
            : undefined,
        [currentChain],
    );

    const walletAddress = useMemo(() => {
        const address = walletInfo?.metaMaskAddress ?? walletInfo?.accounts?.[0];
        return address ? trimAddress(address) : null;
    }, [walletInfo?.metaMaskAddress, walletInfo?.accounts]);

    //Close Profile Modal When Click Outside
    const closeDropDown = (e) => {
        let click = e.target.closest('#DropDownProfileClose');
        let selectNetworkClick = e.target.closest('#SelectNetworkClose');
        if (!click) {
            setIsProfile(false);
        }
        if (!selectNetworkClick) {
            setIsSelectingNetwork(false);
        }
    };

    //Connected Wallet Address
    useEffect(() => {
        if (pathname) {
            dispatch(getPoolByAddress([]));
            dispatch(getTokenByAddress([]));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        walletInfo?.metaMaskAddress,
        walletInfo?.accounts,
        web3Connected,
        pathname,
    ]);

    //Identify Click Event
    useEffect(() => {
        window.addEventListener('click', closeDropDown);
        return () => window.removeEventListener('click', closeDropDown);
    });

    //Handle Add Recent Transaction in LocalStorage
    useEffect(() => {
        if (isRecentDefaultTx) {
            let input = localStorage.getItem('RecentTransactions');
            if (input) {
                if (!!isRecentTx?.length) {
                    let storedList = JSON.parse(input);
                    if (!!storedList.length !== !!isRecentTx?.length) {
                        isRecentTx?.unshift(JSON.parse(input));
                        localStorage.setItem(
                            'RecentTransactions',
                            JSON.stringify(isRecentTx)
                        );
                        setIsRecentTx(isRecentTx);
                    }
                } else {
                    let value = JSON.parse(
                        localStorage.getItem('RecentTransactions')
                    );
                    localStorage.setItem(
                        'RecentTransactions',
                        JSON.stringify([value])
                    );
                    setIsRecentTx([value]);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [RecentTx]);

    //Get Recent Transaction in LocalStorage
    useEffect(() => {
        if (localStorage.getItem('RecentTransactions')) {
            setIsRecentTx(
                JSON.parse(localStorage.getItem('RecentTransactions'))
            );
        }
        setIsRecentDefaultTx(true);
    }, []);

    //Check Recent Tx is found or not
    useEffect(() => {
        if (connectedAddress) {
            let recentTx =
                !!isRecentTx?.length &&
                isRecentTx?.find((i) => i.walletAddress?.toLowerCase() === connectedAddress?.toLowerCase());
            if (recentTx === undefined || !recentTx) {
                setIsTxFound(false);
            } else {
                setIsTxFound(true);
            }
        }
    }, [connectedAddress, isRecentTx, RecentTx]);

    //get block explorer transaction url by transaction info
    const getTransactionUrl = (element) => {
        if (!element?.txHash) return '#';
        const elementChainId = element.chainId ?? defaultChainId;
        const blockExplorerUrl = ChainList.find(
            chain => chain.id === elementChainId
        )?.block_explorer_url;
        return blockExplorerUrl ?
            `${blockExplorerUrl}/tx/${element.txHash}` :
            '#';
    };

    //handle Network Change
    const handleNetworkChange = async (chainId) => {
        if (web3Connected) {
            const hexChainId = '0x' + Number(chainId ?? defaultChainId)
                .toString(16); // chainId must be in hexadecimal numbers
            switchChain(chainId ?? defaultChainId)
                .then(() => {
                    window.location.reload();
                })
                .catch(async (err) => {
                    if (err.code === 4902 || err.code === -32602) {
                        const chainInfo = (chainId && ChainList
                                .find(chain => chain.id === chainId)) ??
                            ChainList
                                .find(chain => chain.id === defaultChainId);
                        console.log(window.ethereum);
                        const modalProvider = await modal.getWalletProvider();
                        console.log(modalProvider);
                        await modalProvider.request({
                            id: 1,
                            jsonrpc: '2.0',
                            method: 'wallet_addEthereumChain',
                            params: [
                                {
                                    chainId: hexChainId,
                                    rpcUrls: [chainInfo.rpc_url],
                                    chainName: chainInfo.name,
                                    nativeCurrency: {
                                        name: chainInfo.native_currency,
                                        symbol: chainInfo.native_currency, // 2-6 characters long
                                        decimals: 18,
                                    },
                                    blockExplorerUrls: chainInfo.block_explorer_url ? [
                                        chainInfo.block_explorer_url,
                                    ] : undefined,
                                },
                            ],
                        });
                    } else {
                        if (err.message) {
                            console.error(err.message, err);
                        } else {
                            console.error(err);
                        }
                    }
                });
        } else {
            const qry = queryString.parse(searchParams);
            const newSearchStr = '?' + queryString.stringify({
                ...qry,
                chainId,
            });
            navigate(newSearchStr);
        }
    };

    //handle Profile/Address DropDown
    const handleDropDown = () => {
        setIsProfile(!isProfile);
    };

    //handle Remove Tx in LocalStorage
    const handleRemoveTx = () => {
        let recentTx =
            !!isRecentTx?.length &&
            isRecentTx?.find((i) => i?.walletAddress === connectedAddress);
        if (recentTx !== undefined && !!isRecentTx.length) {
            isRecentTx?.splice(recentTx, 1);
            localStorage.setItem(
                'RecentTransactions',
                JSON.stringify(isRecentTx)
            );
            handleRemoveTx();
        }
    };
    //Handle Logout from wallet
    const handleLogout = () => {
        setIsProfile(false);
        dispatch(getFaucetToken(''));
        dispatch(getWalletInfo({}));
        dispatch(getWeb3Connected(false));
        dispatch(getConnectedAddress(''));
        dispatch(setCurrentChainId(null));
        handleRemoveTx();
        return disconnect();
    };

    //handle Nav Option Selection
    const handleNavSelection = () => {
        setNavbarCollapsed(true);
        dispatch(
            setIsTradeLoad({
                id: 0,
                status: false,
            })
        );
    };

    return (
        <header>
            <nav className="navbar navbar-expand-lg">
                <div className="container header_block">
                    <div className="logo_bar">
                        <Link to="/" className="navbar-brand d-flex">
                            {theme === 'dark' ? (
                                <img
                                    src={logo}
                                    alt=""
                                    className="img-fluid"
                                />
                            ) : (
                                <img
                                    src={logo_Light}
                                    alt=""
                                    className="img-fluid"
                                />
                            )}
                        </Link>
                    </div>
                    <button
                        className="navbar-toggler border-0"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target="#navbarSupportedContent"
                        aria-controls="navbarSupportedContent"
                        aria-expanded={!navbarCollapsed}
                        aria-label="Toggle navigation"
                        onClick={() => setNavbarCollapsed(!navbarCollapsed)}
                    >
                        <span className="navbar-toggler-icon">
                            <i className="fa-solid fa-bars"></i>
                        </span>
                    </button>
                    <div
                        className={`collapse navbar-collapse ${
                            navbarCollapsed ? '' : 'show'
                        }`}
                        id="navbarSupportedContent"
                    >
                        <ul className="navbar-nav m-auto mb-2 mb-lg-0">
                            {navOptions?.map((option, index) => {
                                return (
                                    !!option?.isVisible && (
                                        <li
                                            className="nav-item namedrop LdMenuOption"
                                            key={index}
                                            onClick={() =>
                                                handleNavSelection()
                                            }
                                        >
                                            <Link
                                                className={`nav-link ${
                                                    (option.path === '/'
                                                        ? pathname === option.path
                                                        : pathname.startsWith(option.path)
                                                    ) ? `active` : ''
                                                }`}
                                                to={option.path}
                                            >
                                                {option?.name}
                                            </Link>
                                        </li>
                                    )
                                );
                            })}
                        </ul>
                    </div>
                    <div className="d-flex justify-content-between align-items-center select_theme">
                        <div className="dMenu1 namedrop me-3">
                            <div className="dropdown_info_block">
                                <div className="lang-select m-0">
                                    <button
                                        className={`btn-select tokenDropDown gap-1 ${
                                            !!currentChain || !!ChainListLoader
                                                ? ''
                                                : 'error_btn'
                                        } ${
                                            (!currentChain || currentChainName)
                                                ? ''
                                                : 'network_Header'
                                        }`}
                                        value="en"
                                        onClick={() => setIsSelectingNetwork(
                                            !isSelectingNetwork
                                        )}
                                        id="SelectNetworkClose"
                                    >
                                        <li>
                                            <div className={`chainTag ${
                                                !!ChainListLoader && !currentChain
                                                    ? 'loader-spine'
                                                    : ''
                                            } ${!ChainListLoader && !currentChain
                                                ? 'rounded-0'
                                                : ''
                                            }`}>
                                                <img
                                                    src={
                                                        !!ChainListLoader && !currentChain
                                                            ? Loader
                                                            : !!currentChain
                                                                ? currentChain.icon
                                                                : alertTriangle
                                                    }
                                                    alt={!!currentChain ? currentChain.name : ''}
                                                />
                                            </div>
                                            <span>
                                                {!!ChainListLoader && !currentChain
                                                    ? 'Loading...'
                                                    : !!currentChain
                                                    ? currentChainName
                                                    : 'Wrong Network'}
                                            </span>
                                        </li>
                                        {isSelectingNetwork ? (
                                            <i className="fa-solid fa-angle-up"/>
                                        ) : (
                                            <i className="fa-solid fa-angle-down"/>
                                        )}
                                    </button>
                                </div>
                                {isSelectingNetwork && (
                                    <div className={`dropdown_info2 scrollbar ${
                                        !currentChain || ChainListLoader || currentChainName
                                            ? 'wide_button'
                                            : ''
                                    }`}>
                                        <div className="inside_desc d-flex flex-column gap-2">
                                            {switchableChains.map(chain => (
                                                <div key={chain.id} className="btn_inside">
                                                    <button
                                                        className={`custom_btn chain_Info ${
                                                            chain.id === currentChain?.id
                                                                ? 'active'
                                                                : 'pointer'
                                                        }`}
                                                        onClick={
                                                            () => handleNetworkChange(chain.id)
                                                        }
                                                    >
                                                        <div className="chainTag">
                                                            <img
                                                                src={chain.icon}
                                                                alt=""
                                                            />
                                                        </div>
                                                        <p className="p-0 m-0">
                                                            {chain.name.split(' ')[0]}
                                                        </p>
                                                    </button>
                                                </div>))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        {!!walletAddress ? (
                                <div className="dMenu1 namedrop1 profileDropDown me-2">
                                    <div className="dropdown_info_block">
                                        <div
                                            className={`d-flex align-items-center justify-content-end dropdown_block_size ${
                                                !!!currentChain && !ChainListLoader
                                                    ? 'error_btn'
                                                    : ''
                                            }`}
                                        >
                                            {!!transactionLoader && (
                                                <div className="loader-spine me-1">
                                                    <img
                                                        src={Loader}
                                                        alt=""
                                                    />
                                                </div>
                                            )}
                                            <button
                                                className="tokenDropDown DropDownProfile"
                                                onClick={handleDropDown}
                                                id="DropDownProfileClose"
                                            >
                                                <span className="desc_pop">
                                                    {walletAddress ?? connectedAddress}
                                                </span>
                                                {!!isProfile ? (
                                                    <i className="fa-solid fa-angle-up"></i>
                                                ) : (
                                                    <i className="fa-solid fa-angle-down"></i>
                                                )}
                                            </button>
                                        </div>

                                        {isProfile && (
                                            <div
                                                className={`${
                                                    !!!currentChain
                                                        ? 'dropdown_info2'
                                                        : 'dropdown_info'
                                                }`}
                                            >
                                                <div className="drop_title themeView">
                                                    <div className="inside_desc">
                                                        <small>
                                                            {walletInfo?.metaMaskAddress &&
                                                                'Connected'}
                                                        </small>
                                                        <span className="desc_pop" onClick={()=> openWeb3Modal({ view: 'Account' })}>
                                                            {walletAddress}
                                                        </span>
                                                    </div>
                                                    <div className="btn_inside">
                                                        <button
                                                            className="custom_btn logout"
                                                            onClick={
                                                                handleLogout
                                                            }
                                                        >
                                                            Logout
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="drop_desc headerTransaction mt-3">
                                                    <span>
                                                        Recent transactions
                                                    </span>
                                                    {!isTxFound && (
                                                        <div className="tokenNotFound RecentTx">
                                                            <p>
                                                                No
                                                                transactions
                                                                Found
                                                            </p>
                                                        </div>
                                                    )}
                                                    {!!isRecentTx &&
                                                        isRecentTx?.map(
                                                            (
                                                                element,
                                                                index
                                                            ) => {
                                                                return (
                                                                    (walletInfo?.metaMaskAddress ===
                                                                        element?.walletAddress ||
                                                                        connectedAddress ===
                                                                            element?.walletAddress) && (
                                                                        <Link
                                                                            to={getTransactionUrl(element)}
                                                                            target={element?.txHash ? '_blank' : undefined}
                                                                            className={`drop_flex ${
                                                                                element?.txHash
                                                                                    ? ''
                                                                                    : 'disable_pointer'
                                                                            }`}
                                                                            key={
                                                                                index
                                                                            }
                                                                        >
                                                                            <p className="float-start">
                                                                                {
                                                                                    element.text
                                                                                }
                                                                            </p>

                                                                            <img
                                                                                src={
                                                                                    element.status ===
                                                                                    2
                                                                                        ? stroke
                                                                                        : element.status ===
                                                                                          1
                                                                                        ? Loader
                                                                                        : alertTriangle
                                                                                }
                                                                                className="float-end"
                                                                                alt=""
                                                                            />
                                                                        </Link>
                                                                    )
                                                                );
                                                            }
                                                        )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                        ) : (
                            <span
                                className="custom_btn pointer Connect_wallet me-2"
                                onClick={() => openWeb3Modal({ view: 'Connect' })}
                            >
                                Connect wallet
                            </span>
                        )}
                        <div data-for={'theme'} data-tip="">
                            {theme === 'dark' ? (
                                <div
                                    className="multiHops-flex justify-content-center"
                                    onClick={() => switchThemes()}
                                >
                                    <span className="SunIcon d-flex">
                                        <img src={SunIcon} alt="" />
                                    </span>
                                </div>
                            ) : (
                                <div
                                    className="multiHops-flex justify-content-center"
                                    onClick={() => switchThemes()}
                                >
                                    <span className="MoonIcon">
                                        <i className="fa-regular fa-moon ms-1"></i>
                                    </span>
                                </div>
                            )}
                        </div>
                        <ReactTooltip
                            id="theme"
                            place="bottom"
                            effect="solid"
                            tooltipRadius="12"
                            border
                            borderColor="true"
                            className="tradeRouteCustomCss"
                            borderClass="borderClass"
                            delayHide={100}
                            delayShow={100}
                            delayUpdate={500}
                            globalEventOff="touchend"
                        >
                            {theme === 'dark'
                                ? 'Light Theme'
                                : 'Dark Theme'}
                        </ReactTooltip>
                    </div>
                </div>
            </nav>
        </header>
    );
};

export default Header;
